import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  IconButton,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { FieldArray } from "react-final-form-arrays";
import { useForm, useFormState } from "react-final-form";
import get from "lodash/get";
import formatAddressObjectFullAddress from "../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import PublicFormGrid from "../../../components/publicForm/PublicFormGrid";
import PublicFormSection from "../../../components/publicForm/PublicFormSection";
import { APP_TOKENS } from "../../../components/publicForm/publicFormTokens";
import RffAddressEditingField from "../../../components/finalForm/inputs/RffAddressEditingField";
import RffBooleanToggleField from "../../../components/finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../../../components/finalForm/inputs/RffDateField";
import RffDollarField from "../../../components/finalForm/inputs/RffDollarField";
import RffNumberField from "../../../components/finalForm/inputs/RffNumberField";
import RffPercentField from "../../../components/finalForm/inputs/RffPercentField";
import RffSelectField from "../../../components/finalForm/inputs/RffSelectField";
import RffSelectToggleField from "../../../components/finalForm/inputs/RffSelectToggleField";
import RffConditional from "../../../components/finalForm/shared/RffConditional";
import { selectOptionsPropertyConditionAtOrigination } from "../../../constants/selectOptions/selectOptionsPropertyConditionAtOrigination";
import { selectOptionsPropertyType } from "../../../constants/selectOptions/selectOptionsPropertyType";
import {
  CONSTRUCTION_PRODUCTS,
  FF_PROPERTY_STATUS_OPTIONS,
  GUC_PROPERTY_STATUS_OPTIONS,
  REFINANCE_PURPOSES,
  SUBJECT_PROPERTY_COUNT_OPTIONS,
} from "../applicationFormShared";

const accordionSx = {
  m: "0 !important",
  borderRadius: "8px",
  boxShadow: `0 0 0 1px ${APP_TOKENS.borderStrong}`,
  overflow: "hidden",
  "&:before": { display: "none" },
};

/**
 * How many property cards each count answer implies. "4+" opens four and lets
 * the applicant add more by hand — the option is open-ended, so four is the
 * floor rather than the answer.
 */
const PROPERTY_COUNT_TARGETS = {
  1: 1,
  2: 2,
  3: 3,
  "4+ properties": 4,
};

/** Step 4 — one accordion per financed property. */
const StepProperties = () => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });

  /**
   * Open enough cards to match the count the applicant just picked.
   *
   * Only ever ADDS. Shrinking the list to match a lower number would silently
   * delete properties they had already filled in; the per-card remove button is
   * the deliberate way to do that.
   */
  const syncPropertyCount = (selected) => {
    const target = PROPERTY_COUNT_TARGETS[selected];
    if (!target) return;
    const current = values.subjectProperties || [];
    if (current.length >= target) return;
    form.change("subjectProperties", [
      ...current,
      ...Array.from({ length: target - current.length }, () => ({})),
    ]);
  };

  return (
  <Flx column>
    <PublicFormSection title="How many properties?">
      <PublicFormGrid>
        <RffSelectToggleField
          name="subjectPropertyCount"
          label="Select the number of properties to be financed for this loan application"
          options={SUBJECT_PROPERTY_COUNT_OPTIONS}
          helperText="Add a card below for each property."
          onChange={syncPropertyCount}
        />
      </PublicFormGrid>
    </PublicFormSection>

    <PublicFormSection
      title="Property Information"
      description="Expand a property to edit it. Questions adapt to your loan product and purpose."
    >
      <FieldArray name="subjectProperties">
        {({ fields: arrayFields, meta }) => (
          <Flx column gap={2}>
            {arrayFields.map((fieldName, index) => (
              <Accordion defaultExpanded key={fieldName} sx={accordionSx}>
                <AccordionSummary
                  sx={{ borderBottom: `1px solid ${APP_TOKENS.borderStrong}` }}
                  expandIcon={
                    <ExpandMoreRounded sx={{ color: APP_TOKENS.ink }} />
                  }
                >
                  <Flx between centerY fullWidth sx={{ pr: 1 }}>
                    <Flx column gap={0.25} sx={{ minWidth: 0 }}>
                      <Txt
                        component="h3"
                        sx={{
                          m: 0,
                          fontSize: 15,
                          fontWeight: 700,
                          color: APP_TOKENS.ink,
                        }}
                      >
                        {`Property #${index + 1}`}
                      </Txt>
                      <PropertyAddressLabel fieldName={fieldName} />
                    </Flx>
                    {/* Only offer removal when a property would remain — the
                        form is meaningless with zero properties. */}
                    {arrayFields.length > 1 && (
                      <IconButton
                        size="small"
                        aria-label={`Remove property ${index + 1}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          arrayFields.remove(index);
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </Flx>
                </AccordionSummary>
                <AccordionDetails
                  sx={{ background: APP_TOKENS.surface, p: 2.8 }}
                >
                  <PropertyFields fieldName={fieldName} />
                </AccordionDetails>
              </Accordion>
            ))}

            <Flx column fullWidth>
              <Button
                variant="text"
                size="medium"
                fullWidth
                startIcon={<Add />}
                onClick={() => arrayFields.push({})}
              >
                Add Property
              </Button>
              {meta.touched && typeof meta.error === "string" && (
                <Txt
                  component="div"
                  sx={{ mt: 0.5, fontSize: 13, color: "error.main" }}
                >
                  {meta.error}
                </Txt>
              )}
            </Flx>
          </Flx>
        )}
      </FieldArray>
    </PublicFormSection>
  </Flx>
  );
};

/**
 * The entered address, under the card's title. With several cards collapsed,
 * "Property #1/#2/#3" alone gives no way to tell which is which — this is the
 * only thing that distinguishes them without expanding each one.
 *
 * Renders nothing until an address exists, so an untouched card stays clean.
 */
const PropertyAddressLabel = ({ fieldName }) => {
  const { values } = useFormState({ subscription: { values: true } });
  const address = formatAddressObjectFullAddress(
    get(values, `${fieldName}.address`),
  );

  if (address === "N/A") return null;

  return (
    <Txt
      component="div"
      sx={{
        fontSize: 12.5,
        fontWeight: 400,
        color: APP_TOKENS.inkMuted,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {address}
    </Txt>
  );
};

/** The per-property question set. `fieldName` is the FieldArray path prefix. */
const PropertyFields = ({ fieldName }) => (
  <PublicFormGrid>
    <RffAddressEditingField
      required
      name={`${fieldName}.address`}
      label="Enter the subject property address"
    />
    <RffSelectToggleField
      name={`${fieldName}.propertyType`}
      label="Property Type"
      options={selectOptionsPropertyType}
    />
    <RffConditional
      field="loanProductType"
      operator="includes"
      value={["Ground Up Construction"]}
    >
      <RffSelectField
        name={`${fieldName}.proposedPropertyType`}
        label="Proposed Property Type"
        options={selectOptionsPropertyType}
      />
    </RffConditional>

    <RffDollarField
      name={`${fieldName}.asIsValue`}
      label="The current estimated as-is or market value of the property"
    />
    <RffConditional
      field="loanProductType"
      operator="includes"
      value={CONSTRUCTION_PRODUCTS}
    >
      <RffDollarField
        name={`${fieldName}.arv`}
        label="After Renovation Value"
      />
    </RffConditional>

    <RffConditional field="loanPurpose" value="Purchase">
      <RffDollarField
        name={`${fieldName}.purchasePrice`}
        label="What is the property purchase price?"
        size={6}
      />
      <RffDateField
        name={`${fieldName}.purchaseDate`}
        label="What is the expected purchase date for the property?"
        size={6}
      />
      <RffDollarField
        name={`${fieldName}.assignmentFees`}
        label="Dollar value of any assignment fees included in purchase contract"
        size={6}
      />
      <RffDollarField
        name={`${fieldName}.sellersConcession`}
        label="Dollar value of any seller's concession included in purchase contract"
        size={6}
      />
    </RffConditional>

    <RffConditional
      field="loanPurpose"
      operator="includes"
      value={REFINANCE_PURPOSES}
    >
      <RffDollarField
        name={`${fieldName}.originalPurchasePrice`}
        label="What was the original purchase price for the property?"
        size={6}
      />
      <RffDateField
        name={`${fieldName}.originalPurchaseDate`}
        label="What was the original purchase date of the property?"
        size={6}
      />
      <RffBooleanToggleField
        name={`${fieldName}.propertyListedForSale`}
        label="Property Listed For Sale"
      />
      <RffDateField
        name={`${fieldName}.lastListedForSaleDate`}
        label="Last Listed For Sale Date"
        size={6}
      />
      <RffDollarField
        name={`${fieldName}.lastListedForSalePrice`}
        label="Last Listed For Sale Price"
        size={6}
      />
    </RffConditional>

    <RffConditional
      operatorForMultiple="AND"
      rules={[
        {
          field: "loanProductType",
          operator: "includes",
          value: CONSTRUCTION_PRODUCTS,
        },
        {
          field: "loanPurpose",
          operator: "includes",
          value: REFINANCE_PURPOSES,
        },
      ]}
    >
      <RffDollarField
        name={`${fieldName}.additionalBasis`}
        label="Any additional costs expended since purchase including costs for permits & plans, land development, renovations. Do not include taxes, insurance, interest expense or other carrying costs"
      />
    </RffConditional>

    <RffBooleanToggleField
      name={`${fieldName}.floodZone`}
      label="Is the property in a flood zone?"
    />
    <RffNumberField
      name={`${fieldName}.residentialSqFootage`}
      label="What is the residential square footage of the property?"
      size={6}
    />
    <RffConditional
      field={`${fieldName}.propertyType`}
      operator="includes"
      value={["Mixed-Use", "Commercial", "Other"]}
    >
      <RffNumberField
        name={`${fieldName}.commercialSqFootage`}
        label="What is the commercial square footage of the property?"
        size={6}
      />
    </RffConditional>
    <RffConditional
      field="loanProductType"
      operator="includes"
      value={CONSTRUCTION_PRODUCTS}
    >
      <RffNumberField
        name={`${fieldName}.afterRenoSqFootage`}
        label="If renovating, the square footage after the project is completed"
        size={6}
      />
    </RffConditional>

    <RffDollarField
      name={`${fieldName}.annualTaxes`}
      label="Annual Taxes"
      size={6}
    />
    <RffDollarField
      name={`${fieldName}.annualInsurance`}
      label="Annual Insurance"
      size={6}
    />
    <RffDollarField
      name={`${fieldName}.annualFloodInsurance`}
      label="Annual Flood Insurance"
      size={6}
    />
    <RffDollarField
      name={`${fieldName}.annualHoaFees`}
      label="Annual HOA Fees"
      size={6}
    />
    <RffConditional
      field="loanProductType"
      operator="includes"
      value={["Stabilized Bridge", "DSCR", "Multifamily Bridge"]}
    >
      <RffDollarField
        name={`${fieldName}.annualPropertyMgmtFees`}
        label="Annual Property Mgmt Fees"
        size={6}
      />
    </RffConditional>
    <RffDollarField
      name={`${fieldName}.annualOtherExpense`}
      label="Annual Other Expense"
      size={6}
    />

    <RffSelectToggleField
      name={`${fieldName}.propertyConditionAtOrigination`}
      label="Property Condition At Origination"
      options={selectOptionsPropertyConditionAtOrigination}
    />

    {/* Project-status questions are product-specific — a DSCR applicant should
        never be asked to describe their "ground up project". */}
    <RffConditional field="loanProductType" value="Fix and Flip">
      <RffSelectField
        name={`${fieldName}.ffPropertyStatus`}
        label="How would you describe the current property status for this fix and flip project?"
        options={FF_PROPERTY_STATUS_OPTIONS}
      />
    </RffConditional>
    <RffConditional field="loanProductType" value="Ground Up Construction">
      <RffSelectField
        name={`${fieldName}.gucPropertyStatus`}
        label="How would you describe the current project status for this ground up project?"
        options={GUC_PROPERTY_STATUS_OPTIONS}
      />
    </RffConditional>
    <RffConditional
      field="loanProductType"
      operator="includes"
      value={CONSTRUCTION_PRODUCTS}
    >
      <RffDollarField
        name={`${fieldName}.constructionBudget`}
        label="Total construction budget for the project including hard and soft costs"
        size={6}
      />
      <RffPercentField
        name={`${fieldName}.financedBudgetPercent`}
        label="What is the requested total percent of construction budget to be financed?"
        size={6}
      />
    </RffConditional>
  </PublicFormGrid>
);

export default StepProperties;
